import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Container, Table, Button, InputGroup, Form, Pagination, Row, Col, Badge, Alert,
} from 'react-bootstrap';
import { AuthContext } from '../context/AuthContext';
import ProtectedSection from '../components/ProtectedSection';
import moncashService from '../services/moncashService';
import MoncashModal from '../components/MoncashModal';
import MoncashViewModal from '../components/MoncashViewModal';
import ReceiptPrintButton from '../components/ReceiptPrintButton';
import { defaultDateRange, validateDateRange } from '../utils/dateFilters';

const typeLabel = (t) => {
  const v = String(t || '').toUpperCase();
  if (v === 'DEPOSIT') return 'Dépôt';
  if (v === 'WITHDRAWAL') return 'Retrait';
  return v || '—';
};
const badgeVariant = (status) => status === 'TERMINATED' ? 'success' : 'secondary';

export default function MoncashPage() {
  const { hasRight } = useContext(AuthContext);
  const canView = hasRight('MONCASH_VIEW');
  const canUpdate = hasRight('MONCASH_UPDATE');
  const canDelete = hasRight('MONCASH_DELETE');

  const initialDates = useMemo(() => defaultDateRange(), []);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [operatorFilter, setOperatorFilter] = useState('');
  const [operators, setOperators] = useState([]);
  const [dateFrom, setDateFrom] = useState(initialDates.dateFrom);
  const [dateTo, setDateTo] = useState(initialDates.dateTo);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [showModal, setShowModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [viewShow, setViewShow] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState('');

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const fetchAll = useCallback(() => {
    if (!canView) return;
    const dateError = validateDateRange(dateFrom, dateTo);
    if (dateError) {
      setErrorMsg(dateError);
      setRows([]);
      setTotal(0);
      return;
    }
    setErrorMsg('');
    moncashService.list({
      page, pageSize, search: filter, type: typeFilter,
      userId: operatorFilter || undefined, dateFrom, dateTo,
    }).then((r) => {
      setRows(r?.data || []);
      setTotal(Number(r?.total || 0));
      const pages = Math.max(1, Number(r?.totalPages || Math.ceil(Number(r?.total || 0) / pageSize)));
      if (page > pages) setPage(pages);
    }).catch((e) => setErrorMsg(e?.response?.data?.message || 'Erreur chargement MonCash.'));
  }, [canView, page, pageSize, filter, typeFilter, operatorFilter, dateFrom, dateTo]);

  const fetchOperators = useCallback(() => {
    if (!canView) return;
    moncashService.operators()
      .then((data) => setOperators(Array.isArray(data) ? data : []))
      .catch(() => setOperators([]));
  }, [canView]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { fetchOperators(); }, [fetchOperators]);
  useEffect(() => { setPage(1); }, [filter, typeFilter, operatorFilter, dateFrom, dateTo, pageSize]);

  const paginationItems = useMemo(() => {
    const items = [
      <Pagination.First key="first" disabled={page === 1} onClick={() => setPage(1)} />,
      <Pagination.Prev key="prev" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} />,
    ];
    const from = Math.max(1, page - 2);
    const to = Math.min(totalPages, page + 2);
    if (from > 1) items.push(<Pagination.Ellipsis key="e1" disabled />);
    for (let p = from; p <= to; p += 1) {
      items.push(<Pagination.Item key={p} active={p === page} onClick={() => setPage(p)}>{p}</Pagination.Item>);
    }
    if (to < totalPages) items.push(<Pagination.Ellipsis key="e2" disabled />);
    items.push(
      <Pagination.Next key="next" disabled={page === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} />,
      <Pagination.Last key="last" disabled={page === totalPages} onClick={() => setPage(totalPages)} />
    );
    return items;
  }, [page, totalPages]);

  const openCreate = () => { setEditRow(null); setShowModal(true); };
  const openEdit = (r) => { setEditRow(r); setShowModal(true); };
  const save = (payload) => {
    setErrorMsg('');
    const op = editRow?.id ? moncashService.update(editRow.id, payload) : moncashService.create(payload);
    op.then(() => { setShowModal(false); setEditRow(null); fetchAll(); fetchOperators(); })
      .catch((e) => setErrorMsg(e?.response?.data?.message || 'Erreur enregistrement MonCash.'));
  };
  const remove = (r) => {
    if (!window.confirm('Supprimer cette transaction MonCash ?')) return;
    setErrorMsg('');
    moncashService.remove(r.id).then(() => { fetchAll(); fetchOperators(); })
      .catch((e) => setErrorMsg(e?.response?.data?.message || 'Erreur suppression.'));
  };
  const openView = (r) => {
    if (!r?.id) return;
    setViewShow(true); setViewRow(null); setViewError(''); setViewLoading(true);
    moncashService.get(r.id).then(setViewRow)
      .catch((e) => setViewError(e?.response?.data?.message || 'Erreur chargement détails.'))
      .finally(() => setViewLoading(false));
  };
  const closeView = () => { setViewShow(false); setViewRow(null); setViewError(''); setViewLoading(false); };
  const nameForRow = (r) => r.client
    ? `${r.client.firstName || ''} ${r.client.lastName || ''}`.trim()
    : (r.clientName || 'Non-client');

  const buildReceiptDocument = async (r) => {
    const full = await moncashService.get(r.id);
    const clientLabel = full?.client
      ? `${full.client.firstName || ''} ${full.client.lastName || ''}`.trim()
      : (full?.clientName || 'Non-client');
    const type = String(full?.type || '').toUpperCase();
    const createdBy = full?.createdByUser?.username || '—';
    return {
      title: `Reçu MonCash${full?.id ? ` - #${full.id}` : ''}`,
      rows: [
        { label: 'ID transaction', value: full?.id || '—' },
        { label: 'Type', value: typeLabel(type) },
        { label: 'Statut', value: full?.status || '—' },
        { label: 'Téléphone', value: full?.phone || '—' },
        { label: 'Client', value: clientLabel },
        { label: 'Montant saisi', value: `${Number(full?.amountInputHTG || 0).toFixed(2)} HTG` },
        { label: 'Frais', value: `${Number(full?.feeHTG || 0).toFixed(2)} HTG` },
        { label: 'Frais payé séparément', value: full?.feeIncluded ? 'Oui' : 'Non' },
        { label: type === 'DEPOSIT' ? 'Crédit MonCash' : 'Débit MonCash', value: `${Number(full?.amountAccountHTG || 0).toFixed(2)} HTG` },
        { label: type === 'DEPOSIT' ? 'Cash encaissé' : 'Cash décaissé', value: `${Number(full?.amountCashHTG || 0).toFixed(2)} HTG` },
        { label: 'Créée le', value: full?.createdAt ? new Date(full.createdAt).toLocaleString() : '—' },
        { label: 'Utilisateur', value: createdBy },
      ],
      signatures: { client: 'Signature client', cashier: createdBy },
    };
  };

  return (
    <Container className="pt-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h2>MonCash</h2>
        <ProtectedSection code="MONCASH_CREATE"><Button onClick={openCreate}>+ Nouvelle MonCash</Button></ProtectedSection>
      </div>
      {!canView && <Alert variant="warning">Vous n’avez pas le droit de voir les transactions MonCash.</Alert>}
      {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}

      <Row className="g-2 mb-3 align-items-end">
        <Col md={3}>
          <Form.Label>Date début</Form.Label>
          <Form.Control type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </Col>
        <Col md={3}>
          <Form.Label>Date fin</Form.Label>
          <Form.Control type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </Col>
        <Col md={6}>
          <Form.Label>Recherche</Form.Label>
          <InputGroup>
            <Form.Control placeholder="Téléphone, client, note, utilisateur..." value={filter} onChange={(e) => setFilter(e.target.value)} />
            <Button variant="outline-secondary" onClick={() => setFilter('')}>×</Button>
          </InputGroup>
        </Col>
        <Col md={4}>
          <Form.Label>Type</Form.Label>
          <Form.Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">Tous types</option><option value="DEPOSIT">Dépôt</option><option value="WITHDRAWAL">Retrait</option>
          </Form.Select>
        </Col>
        <Col md={4}>
          <Form.Label>Traité par</Form.Label>
          <Form.Select value={operatorFilter} onChange={(e) => setOperatorFilter(e.target.value)}>
            <option value="">Tous les utilisateurs</option>
            {operators.map((u) => <option key={u.id} value={u.id}>{u.username}</option>)}
          </Form.Select>
        </Col>
        <Col md={4}>
          <Form.Label>Affichage</Form.Label>
          <Form.Select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
            <option value={10}>10 lignes</option><option value={25}>25 lignes</option><option value={50}>50 lignes</option><option value={100}>100 lignes</option>
          </Form.Select>
        </Col>
      </Row>

      <Table hover responsive>
        <thead><tr><th>Date</th><th>Type</th><th>Téléphone</th><th>Client</th><th className="text-end">Montant saisi</th><th className="text-end">Frais</th><th className="text-end">Compte</th><th className="text-end">Cash</th><th>Traité par</th><th>Statut</th><th style={{ width: 420 }}>Actions</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td>{r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}</td><td>{typeLabel(r.type)}</td><td>{r.phone}</td><td>{nameForRow(r)}</td>
              <td className="text-end">{Number(r.amountInputHTG || 0).toFixed(2)}</td>
              <td className="text-end">{Number(r.feeHTG || 0).toFixed(2)} {r.feeIncluded ? '(séparés)' : '(retenus)'}</td>
              <td className="text-end">{Number(r.amountAccountHTG || 0).toFixed(2)}</td><td className="text-end">{Number(r.amountCashHTG || 0).toFixed(2)}</td>
              <td>{r.createdByUser?.username || '—'}</td>
              <td><Badge bg={badgeVariant(r.status)}>{r.status}</Badge></td>
              <td>
                {canView && <Button size="sm" variant="outline-primary" className="me-2 mb-1" onClick={() => openView(r)}>Voir</Button>}
                <ReceiptPrintButton className="me-2 mb-1" getDocument={() => buildReceiptDocument(r)} />
                {canUpdate && <Button size="sm" variant="outline-secondary" className="me-2 mb-1" onClick={() => openEdit(r)}>Modifier</Button>}
                {canDelete && <Button size="sm" variant="outline-danger" className="mb-1" onClick={() => remove(r)}>Supprimer</Button>}
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={11} className="text-center text-muted">Aucun résultat.</td></tr>}
        </tbody>
      </Table>

      <Row className="mt-2"><Col md={4}><span className="text-muted">Résultats : {total} • Page {page} / {totalPages}</span></Col><Col md={8} className="d-flex justify-content-md-end"><Pagination size="sm" className="mb-0">{paginationItems}</Pagination></Col></Row>

      <MoncashModal show={showModal} onHide={() => setShowModal(false)} onSave={save} initialData={editRow} />
      <MoncashViewModal show={viewShow} onHide={closeView} row={viewRow} loading={viewLoading} error={viewError} />
    </Container>
  );
}
